import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCrewsAndEmployees } from '../../api/jobWorkflow'
import type { EmployeeOption } from '../../api/jobWorkflow'
import type { AssignmentSource } from '../../api/jobAssignmentDefaults'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

export interface ContractAssignmentData {
  /** Empty string = not chosen yet (required on Create). */
  assignment_source: AssignmentSource | ''
  default_assigned_crew_id: string
  default_assigned_employee_id: string
}

interface ContractAssignmentFieldsProps {
  value: ContractAssignmentData
  onChange: (value: ContractAssignmentData) => void
  /** Resolved property default labels when using property_default */
  propertyDefaultLabels?: {
    crewName: string | null
    employeeName: string | null
  } | null
  hasProperty?: boolean
  /** When true, native required + empty option (Create / unset Edit). */
  required?: boolean
}

export function ContractAssignmentFields({
  value,
  onChange,
  propertyDefaultLabels,
  hasProperty = false,
  required = true,
}: ContractAssignmentFieldsProps) {
  const [crews, setCrews] = useState<{ id: string; name: string }[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])

  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    fetchCrewsAndEmployees().then(({ crews: c, employees: e, loadError: err }) => {
      setCrews(c)
      setEmployees(e)
      if (err) setLoadError(err)
      else if (c.length === 0 && e.length === 0) setLoadError('empty')
      else setLoadError(null)
    })
  }, [])

  const set = (patch: Partial<ContractAssignmentData>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assignment source{required ? ' *' : ''}
        </label>
        <select
          required={required}
          value={value.assignment_source || ''}
          onChange={(e) =>
            set({ assignment_source: (e.target.value || '') as AssignmentSource | '' })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="" disabled={required}>
            Select assignment source…
          </option>
          <option value="property_default">Use property default</option>
          <option value="custom">Custom for this contract</option>
          <option value="unassigned">Dispatcher assigns (leave unassigned)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Contract-level default for jobs. Exterior / Interior visit assignees (below) are separate
          overrides on visit groups.
        </p>
      </div>

      {value.assignment_source === 'property_default' ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          {!hasProperty ? (
            <p>Select a property to see its default assignment.</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <dt className="font-medium text-gray-600">Property default crew</dt>
                <dd>{propertyDefaultLabels?.crewName || 'None set on property'}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Property default employee</dt>
                <dd>{propertyDefaultLabels?.employeeName || 'None set on property'}</dd>
              </div>
            </dl>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Changing the property defaults (or switching to Custom) updates future open jobs on this
            contract.
          </p>
        </div>
      ) : value.assignment_source === 'custom' ? (
        <div className="space-y-3">
          {loadError && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {loadError === 'empty' ? (
                <>
                  No crews or employees found.{' '}
                  <Link to="/settings/team" className="underline font-medium">
                    Add them in Team
                  </Link>
                  .
                </>
              ) : (
                loadError
              )}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Crew</label>
              <select
                value={value.default_assigned_crew_id || ''}
                onChange={(e) => set({ default_assigned_crew_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Unassigned</option>
                {crews.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Employee</label>
              <select
                value={value.default_assigned_employee_id || ''}
                onChange={(e) => set({ default_assigned_employee_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Unassigned</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {formatEmployeeLabel(e)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : value.assignment_source === 'unassigned' ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          Jobs remain unassigned for dispatch unless a visit group has its own crew or employee
          override.
        </div>
      ) : null}
    </div>
  )
}
